/**
 * LinkedInCard — connexion du compte LinkedIn, trois états + deux bandeaux.
 *
 *   non configuré  → « mode simulation », aucun bouton
 *   configuré      → bouton « Connecter LinkedIn »
 *   connecté       → nom + bouton « Déconnecter »
 *
 * Bandeaux : ambre à moins de 14 jours d'expiration, rouge une fois expirée.
 *
 * IMPORTANT sur l'expiration : LinkedIn ne délivre de refresh token qu'aux
 * partenaires Marketing Developer Platform approuvés. Pour une application
 * « Share on LinkedIn » ordinaire, la reconnexion tous les 60 jours est le
 * fonctionnement NOMINAL, pas un incident. Le texte du bandeau le dit
 * clairement plutôt que de laisser croire à une panne.
 */
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, Copy, ExternalLink, Linkedin, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Sommes-nous affichés à l'intérieur d'une autre application ?
 *
 * Break News tourne dans la WebView d'Apollo Global. LinkedIn REFUSE d'afficher
 * sa page d'autorisation dans un cadre — protection anti-clickjacking commune à
 * tous les fournisseurs OAuth, non contournable. Naviguer sur place produit
 * « www.linkedin.com n'autorise pas la connexion ».
 *
 * La comparaison est sûre même en cross-origin ; lire window.top.location, non.
 */
function isEmbedded() {
  try {
    return window.top !== window.self;
  } catch {
    return true; // accès refusé = on est bien dans un cadre étranger
  }
}

export default function LinkedInCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [manualUrl, setManualUrl] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/linkedin/status");
      setState(data);
    } catch {
      // L'échec du statut ne doit jamais casser la page : on masque la carte.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async () => {
    setBusy(true);
    setManualUrl(null);
    try {
      const { data } = await api.get("/linkedin/auth-url");
      const url = data.auth_url;

      // Hors cadre (navigateur classique) : navigation directe, inchangé.
      if (!isEmbedded()) {
        window.location.href = url;
        return;
      }

      // Dans la WebView Apollo : il faut SORTIR du cadre. On reste dans le
      // geste utilisateur, seul contexte où window.open a une chance d'être
      // honoré par une WebView.
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (opened) {
        setBusy(false);
        toast.info("Terminez la connexion dans l'onglet LinkedIn qui vient de s'ouvrir.");
        return;
      }

      // La WebView a bloqué l'ouverture : on donne le lien à la personne.
      setManualUrl(url);
      setBusy(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Impossible de démarrer la connexion LinkedIn."
      );
      setBusy(false);
    }
  };

  const copyManualUrl = async () => {
    try {
      await navigator.clipboard.writeText(manualUrl);
      toast.success("Lien copié. Collez-le dans Safari ou Chrome.");
    } catch {
      toast.error("Copie impossible — sélectionnez le lien à la main.");
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/linkedin/disconnect");
      toast.success("Compte LinkedIn déconnecté.");
      await load();
    } catch {
      toast.error("Déconnexion impossible.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className="rounded-[12px] p-5 bg-white mb-4"
        style={{ border: "1px solid #E5E5E5" }}
        data-testid="linkedin-card-loading"
      >
        <Loader2 size={16} className="animate-spin" color="#F77F00" />
      </div>
    );
  }

  // En production, tant que l'intégration n'est pas configurée, la carte
  // n'existe pas : inutile d'exposer un « mode simulation » aux membres du
  // réseau. Le backend décide (visible = configured || is_dev).
  if (!state || state.visible === false) return null;

  const { configured, connected, name, expiring_soon, days_until_expiry,
          reauth_required, auto_renewable, missing_settings, admin_only } = state;

  return (
    <div
      className="rounded-[12px] p-5 bg-white mb-4"
      style={{ border: "1px solid #E5E5E5" }}
      data-testid="linkedin-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Linkedin size={18} color="#0A66C2" />
        <h3 className="font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
          LinkedIn
        </h3>
      </div>

      {/* --- Bandeau rouge : autorisation expirée --------------------------- */}
      {reauth_required && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-3 text-sm"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}
          data-testid="linkedin-banner-expired"
        >
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <strong>Reconnexion LinkedIn nécessaire.</strong> Votre autorisation
            a expiré. Une reconnexion repart pour 60 jours.
          </div>
        </div>
      )}

      {/* --- Bandeau ambre : expiration proche ------------------------------ */}
      {!reauth_required && expiring_soon && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg mb-3 text-sm"
          style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}
          data-testid="linkedin-banner-expiring"
        >
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            Il reste <strong>{days_until_expiry} jour
            {days_until_expiry > 1 ? "s" : ""}</strong> d'autorisation.
            {auto_renewable
              ? " Le renouvellement est automatique."
              : " Reconnectez maintenant en un clic, ça repart pour 60 jours."}
          </div>
        </div>
      )}

      {/* --- État 1 : non configuré ----------------------------------------- */}
      {!configured && (
        <div data-testid="linkedin-state-mock">
          {admin_only ? (
            <>
              <p className="text-sm" style={{ color: "#1A1A1A" }}>
                <strong>Intégration installée, en attente d'activation.</strong>{" "}
                La publication ne partira pas tant que les variables
                d'environnement ne sont pas posées.
              </p>
              <p className="text-xs mt-2" style={{ color: "#999" }}>
                Cette carte n'est visible que par la direction — les membres du
                réseau ne voient rien tant que l'activation n'est pas faite.
              </p>
            </>
          ) : (
            <p className="text-sm" style={{ color: "#666" }}>
              Connexion LinkedIn — <strong>mode simulation</strong>. La publication
              est simulée : rien n'est envoyé sur LinkedIn.
            </p>
          )}
          {missing_settings?.length > 0 && (
            <div
              className="mt-3 p-2.5 rounded-lg text-xs"
              style={{ background: "#FAFAFA", border: "1px solid #E5E5E5", color: "#666" }}
              data-testid="linkedin-missing-settings"
            >
              <div className="font-semibold mb-1" style={{ color: "#1A1A1A" }}>
                Variables manquantes
              </div>
              <ul className="list-disc list-outside pl-4 space-y-0.5">
                {missing_settings.map((k) => (
                  <li key={k}>
                    <code>{k}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* --- État 2 : configuré, non connecté -------------------------------- */}
      {configured && !connected && (
        <div data-testid="linkedin-state-disconnected">
          <p className="text-sm mb-3" style={{ color: "#666" }}>
            Connectez votre compte pour publier une Breaking News sur votre
            profil LinkedIn personnel.
          </p>
          <button
            onClick={connect}
            disabled={busy}
            data-testid="linkedin-connect"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "#0A66C2" }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Linkedin size={14} />}
            Connecter LinkedIn
          </button>

          {/* Repli : la WebView a refusé d'ouvrir une fenêtre. On donne le lien. */}
          {manualUrl && (
            <div
              className="mt-3 p-3 rounded-lg"
              style={{ background: "#FFF8F0", border: "1px solid #F5D9B8" }}
              data-testid="linkedin-manual-link"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} color="#F77F00" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm" style={{ color: "#1A1A1A" }}>
                  <p>
                    LinkedIn refuse de s'afficher à l'intérieur de l'application
                    Apollo. Ouvrez cette page dans <strong>Safari</strong> ou{" "}
                    <strong>Chrome</strong> : une fois le compte connecté, tout
                    fonctionnera ici, y compris sur mobile.
                  </p>
                  <p className="mt-1.5 text-xs" style={{ color: "#666" }}>
                    À la fin, le navigateur vous ramènera sur Break News et
                    pourra vous redemander de vous identifier — c'est sans
                    importance, votre compte LinkedIn est déjà enregistré.
                    Fermez l'onglet, revenez ici et touchez « J'ai terminé ».
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <a
                  href={manualUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="linkedin-manual-open"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white"
                  style={{ background: "#0A66C2" }}
                >
                  <ExternalLink size={14} />
                  Ouvrir dans le navigateur
                </a>
                <button
                  type="button"
                  onClick={copyManualUrl}
                  data-testid="linkedin-manual-copy"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border bg-white"
                  style={{ borderColor: "#E5E5E5", color: "#1A1A1A" }}
                >
                  <Copy size={14} />
                  Copier le lien
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await load();
                    setManualUrl(null);
                  }}
                  data-testid="linkedin-manual-done"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border bg-white"
                  style={{ borderColor: "#E5E5E5", color: "#1A1A1A" }}
                >
                  <RefreshCw size={14} />
                  J'ai terminé
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- État 3 : connecté ---------------------------------------------- */}
      {configured && connected && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap"
          data-testid="linkedin-state-connected"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {state.picture ? (
              <img src={state.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "#E8F1FB" }}
              >
                <Linkedin size={14} color="#0A66C2" />
              </span>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">
                LinkedIn connecté
              </div>
              <div className="text-xs truncate" style={{ color: "#666" }}>
                {name || "Compte LinkedIn"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(reauth_required || expiring_soon) && !auto_renewable && (
              <button
                onClick={connect}
                disabled={busy}
                data-testid="linkedin-reconnect"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "#0A66C2" }}
              >
                <RefreshCw size={13} /> Reconnecter
              </button>
            )}
            <button
              onClick={disconnect}
              disabled={busy}
              data-testid="linkedin-disconnect"
              className="px-3 py-1.5 rounded-full text-sm font-semibold border disabled:opacity-60"
              style={{ borderColor: "#E5E5E5", color: "#666" }}
            >
              Déconnecter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
